import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { RootState, AppDispatch } from "../../../redux/store";
import { fetchAllUsers } from "../../../redux/authSlice";

import { listenAllSupportThreads } from "../../../redux/supportSlice";

const CommerceManagementSupport: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const currentUser = useSelector((s: RootState) => s.auth.user);
  const allUsers = useSelector((s: RootState) => s.auth.allUsers);
  const allThreads = useSelector((s: RootState) => (s.support as any).allThreads);

  const supportId = currentUser?.userId;
  const isSupport =
    currentUser?.isSupport === true ||
    currentUser?.isMaster === true ||
    currentUser?.role?.includes?.("support");

  // Load all users (for avatar & name info)
  useEffect(() => {
    if (allUsers.length === 0) dispatch(fetchAllUsers());
  }, [dispatch, allUsers.length]);

  // Listen to all support threads
  useEffect(() => {
    if (isSupport && supportId) {
      dispatch(listenAllSupportThreads({ supportId }));
    }
  }, [dispatch, supportId, isSupport]);

  const getInitials = (firstName?: string, lastName?: string) =>
    `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();

  if (!isSupport) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <h1 style={styles.headerTitle}>Support Inbox</h1>
        </header>
        <div style={styles.denied}>
          🚫 Unauthorized: Support role required.
        </div>
      </div>
    );
  }

  return (
    <>
      <header style={styles.header}>
        <h1 style={styles.headerTitle}>Support Inbox</h1>
      </header>

      <div style={styles.page}>
        {allThreads.map((thread: any) => {
          const otherUserId = thread.userId;
          const user = allUsers.find((u) => u.userId === otherUserId);

          return (
            <div
              key={thread.id}
              style={styles.convoCard}
              onClick={() => navigate(`/supportinbox/${supportId}/${otherUserId}`)}
            >
              {/* Avatar */}
              {user?.profilePicture ? (
                <img src={user.profilePicture} style={styles.avatar} />
              ) : (
                <div style={styles.avatarInitials}>
                  {getInitials(user?.firstName, user?.lastName)}
                </div>
              )}

              {/* Name + Last message */}
              <div style={styles.textBlock}>
                <h4 style={styles.name}>
                  {user?.firstName} {user?.lastName}
                </h4>
                <p style={styles.preview}>
                  {thread.lastMessage?.text || "Say hello 👋"}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default CommerceManagementSupport;

const HEADER_HEIGHT = 70;

const styles: { [key: string]: React.CSSProperties } = {
  header: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: HEADER_HEIGHT,
    background: "#0a0a0a",
    borderBottom: "1px solid #222",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  },
  headerTitle: {
    color: "#e0e0e0",
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: 1.2,
  },
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg,#000,#111,#1a1a1a)",
    color: "#fff",
    padding: `${HEADER_HEIGHT + 24}px 20px`,
  },
  denied: {
    textAlign: "center",
    marginTop: "100px",
    fontSize: 18,
    color: "#ff4d4d",
  },
  convoCard: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#111",
    border: "1px solid #333",
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    objectFit: "cover",
    marginRight: 12,
  },
  avatarInitials: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    background: "#222",
    color: "#ccc",
    fontWeight: 700,
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  textBlock: {
    flex: 1,
    overflow: "hidden",
  },
  name: {
    margin: 0,
    fontSize: "1rem",
    fontWeight: 600,
    color: "#bf9b30",
  },
  preview: {
    margin: 0,
    fontSize: "0.9rem",
    color: "#ccc",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};
